import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AvailableModel, ModelBinding, ModelUseType } from '../../../types/backend';
import { useAgentSpecService } from '../services/agentSpecService';
import { useModelBindingService } from '../services/modelBindingService';
import { agentSpecKeys } from './useAgentSpecForm';

export const modelBindingKeys = {
  list: () => ['prompt-studio', 'model-bindings'] as const,
};

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/**
 * The model each non-agent prompt use runs on (PS-UC-17). One draft per use, saved one row at a
 * time; a use with no stored row has no model and no default is put in its place (PRIN-02).
 */
export function useModelBindings({ active }: { active: boolean }) {
  const bindingService = useModelBindingService();
  const agentSpecService = useAgentSpecService();
  const queryClient = useQueryClient();

  const bindingsQuery = useQuery<ModelBinding[]>({
    queryKey: modelBindingKeys.list(),
    queryFn: () => bindingService.listModelBindings(),
    enabled: active,
  });

  // The same catalog and cache entry the agent editor reads, so both offer the same models.
  const modelsQuery = useQuery<AvailableModel[]>({
    queryKey: agentSpecKeys.models(),
    queryFn: () => agentSpecService.listAvailableModels(),
    enabled: active,
  });

  const [drafts, setDrafts] = useState<Partial<Record<ModelUseType, string>>>({});
  const [errors, setErrors] = useState<Partial<Record<ModelUseType, string>>>({});
  const [savedUse, setSavedUse] = useState<ModelUseType | null>(null);
  // Pending per use: one mutation observer only remembers its latest call, so a second row's
  // Save would otherwise clear the first row's in-flight state.
  const [pending, setPending] = useState<Partial<Record<ModelUseType, boolean>>>({});

  const stored = (use: ModelUseType): string | null =>
    bindingsQuery.data?.find((b) => b.use === use)?.model_id ?? null;

  const saveMutation = useMutation({
    mutationFn: (req: { use: ModelUseType; model_id: string }) => bindingService.updateModelBinding(req),
    onSuccess: (updated) => {
      queryClient.setQueryData<ModelBinding[]>(modelBindingKeys.list(), (prev) => [
        ...(prev ?? []).filter((b) => b.use !== updated.use),
        updated,
      ]);
      setDrafts((prev) => ({ ...prev, [updated.use]: undefined }));
      setSavedUse(updated.use);
    },
    onError: (err, req) => setErrors((prev) => ({ ...prev, [req.use]: message(err) })),
    onSettled: (_data, _err, req) => setPending((prev) => ({ ...prev, [req.use]: false })),
  });

  const choose = (use: ModelUseType, modelId: string) => {
    setDrafts((prev) => ({ ...prev, [use]: modelId }));
    setErrors((prev) => ({ ...prev, [use]: undefined }));
    setSavedUse(null);
  };

  const save = (use: ModelUseType) => {
    const modelId = drafts[use];
    if (!modelId || pending[use]) return;
    setErrors((prev) => ({ ...prev, [use]: undefined }));
    setPending((prev) => ({ ...prev, [use]: true }));
    saveMutation.mutate({ use, model_id: modelId });
  };

  const loadError = bindingsQuery.isError
    ? `Could not read the model bindings: ${message(bindingsQuery.error)}`
    : null;

  // A failed catalog read is not an empty catalog: the rows must not claim what is or is not
  // offered when the answer is unknown (FE-14).
  const modelsError = modelsQuery.isError
    ? `Could not read the model catalog: ${message(modelsQuery.error)}`
    : null;

  return {
    loading: bindingsQuery.isLoading,
    loadError,
    models: modelsQuery.data ?? [],
    modelsLoading: modelsQuery.isLoading,
    modelsError,
    stored,
    selected: (use: ModelUseType): string | null => drafts[use] ?? stored(use),
    dirty: (use: ModelUseType): boolean => drafts[use] !== undefined && drafts[use] !== stored(use),
    saving: (use: ModelUseType): boolean => pending[use] === true,
    error: (use: ModelUseType): string | null => errors[use] ?? null,
    savedUse,
    choose,
    save,
  };
}
